import { CheckCircle2, XCircle, AlertTriangle, Ban, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONFIGS = {
  SUCCESS:  { icon: CheckCircle2,    title: "DRINK REDEEMED",       bg: "bg-emerald-950", border: "border-emerald-500", text: "text-emerald-300", accent: "text-emerald-400", tid: "scan-success" },
  NO_DRINKS:{ icon: AlertTriangle,   title: "NO MORE DRINKS AVAILABLE",  bg: "bg-amber-950",   border: "border-amber-500",   text: "text-amber-200",   accent: "text-amber-400",   tid: "scan-no-drinks" },
  INVALID:  { icon: XCircle,         title: "INVALID TICKET",       bg: "bg-rose-950",    border: "border-rose-600",    text: "text-rose-200",    accent: "text-rose-400",    tid: "scan-invalid" },
  INACTIVE: { icon: Ban,             title: "TICKET INACTIVE",      bg: "bg-slate-900",   border: "border-slate-600",   text: "text-slate-200",   accent: "text-slate-300",   tid: "scan-inactive" },
  DUPLICATE:{ icon: RefreshCcw,      title: "DUPLICATE SCAN",       bg: "bg-orange-950",  border: "border-orange-500",  text: "text-orange-200",  accent: "text-orange-400",  tid: "scan-duplicate" },
  NOT_YET:  { icon: AlertTriangle,   title: "NOT AVAILABLE YET",    bg: "bg-amber-950",   border: "border-amber-500",   text: "text-amber-200",   accent: "text-amber-400",   tid: "scan-not-yet" },
  INSUFFICIENT_CREDIT: { icon: AlertTriangle, title: "NOT ENOUGH CREDIT", bg: "bg-amber-950", border: "border-amber-500", text: "text-amber-200", accent: "text-amber-400", tid: "scan-insufficient-credit" },
};

const DRINK_LABEL = { SOFT_DRINK: "SOFT DRINK", BEER: "BEER", COCKTAIL: "COCKTAIL" };

export default function ScanResultModal({ result, onDismiss }) {
  if (!result) return null;
  const cfg = CONFIGS[result.result] || CONFIGS.INVALID;
  const Icon = cfg.icon;
  const t = result.ticket;
  const src = result.credit_source || result.redemption?.credit_source;
  const drink = result.redemption?.drink_type;
  const credit = t ? (t.credit_cents || 0) / 100 : 0;
  const SRC = { ADDITIONAL_PURCHASED: "Earlier Purchased Drink", CREDIT: `Paid with credit · -$${((result.redemption?.credit_amount_cents || 0) / 100).toFixed(2)}`, BASE: "Included Drink" };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md" onClick={onDismiss} data-testid={cfg.tid}>
      <div className={`fb-pop w-full max-w-2xl ${cfg.bg} border-4 ${cfg.border} rounded-3xl p-8 sm:p-12 shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center mb-6">
          <Icon className={`${cfg.accent}`} size={96} strokeWidth={2.2} />
        </div>

        <h2 className={`font-display text-4xl sm:text-6xl font-black text-center ${cfg.text} tracking-tight`} data-testid="scan-result-title">
          {cfg.title}
        </h2>

        {result.result === "SUCCESS" && drink && (
          <div className="mt-3 text-center">
            <div className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight" data-testid="scan-result-drink">{DRINK_LABEL[drink]}</div>
            {src && (
              <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase ${src !== "BASE" ? "bg-orange-500/20 text-orange-300 border border-orange-500/40" : "bg-slate-800 text-slate-300 border border-slate-700"}`} data-testid="scan-result-source">
                {SRC[src] || "Included Drink"}
              </div>
            )}
          </div>
        )}

        {result.message && ["NOT_YET", "INSUFFICIENT_CREDIT", "NO_DRINKS"].includes(result.result) && (
          <div className={`mt-3 text-center text-lg font-bold ${cfg.text}`} data-testid="scan-result-message">{result.message.replace(/^NOT ENOUGH CREDIT — /, "")}</div>
        )}

        {t && (
          <div className="mt-6 space-y-2 text-center">
            <div className={`text-xs tracking-[0.3em] ${cfg.accent}`}>TICKET</div>
            <div className="font-display text-xl sm:text-2xl font-bold text-white" data-testid="scan-result-ticket-id">
              {t.ticket_id} · {t.ticket_type}
            </div>
            {t.guest_name && <div className="text-slate-300">{t.guest_name}</div>}
            {t.drinks_remaining === 0 && credit <= 0 && result.result !== "NOT_YET" && (
              <div className="text-amber-200 text-sm" data-testid="scan-result-no-credits">This {t.ticket_type === "EMPLOYEE" ? "Employee " : ""}ticket has no remaining drink credits.</div>
            )}

            <div className={`mt-4 grid ${t.ticket_type === "EMPLOYEE" ? "grid-cols-1" : "grid-cols-2"} gap-4`}>
              <div>
                <div className={`text-xs tracking-[0.3em] ${cfg.accent}`}>INCLUDED LEFT</div>
                <div className={`font-display text-7xl font-black ${cfg.text}`} data-testid="scan-result-remaining">{t.drinks_remaining}</div>
              </div>
              {t.ticket_type !== "EMPLOYEE" && <div>
                <div className={`font-display text-5xl sm:text-6xl font-black ${cfg.text} mt-2`} data-testid="scan-result-credit">${credit.toFixed(2)}</div>
              </div>}
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <Button onClick={onDismiss} data-testid="scan-result-dismiss-btn"
            className="bg-white text-slate-900 hover:bg-slate-200 font-bold px-10 py-6 text-lg rounded-xl">
            READY FOR NEXT TICKET
          </Button>
        </div>
      </div>
    </div>
  );
}
